import { useCalculatorConsts } from "../useCalculatorConsts";
import { useCalculatorFxUtils } from "../useCalculatorFxUtils";
import type { Offer, FxValues, Customizations, LowPersonalShareIncreasingMargin } from "@/models/simulation/subtypes";
import { type SimulationDemand } from "@/models/Demand";
import type { CalculatorOfferInstallmentSegment } from "@/types/calculator";

type Tuple<T, N extends number, R extends T[] = []> = R['length'] extends N ? R : Tuple<T, N, [...R, T]>;
type CreateOfferInstallmentSegments = (
  offer: Offer,
  userInput: Nullable<SimulationDemand>,
  options?: {
    /** value → used as-is; null → no LPS; undefined → falls back to offer's LPS */
    customLps?: Nullable<LowPersonalShareIncreasingMargin>;
  },
) => CalculatorOfferInstallmentSegment[];

export const useCalculatorOfferInstallmentSegments = () => {
  const { FROM_PERIOD } = useCalculatorConsts();
  const { getCellAmountFx } = useCalculatorFxUtils();

  /**
   *  Splits the loan period into installment segments at fixed-rate and LPS margin boundaries.
   *  Each segment defines its end period, installment amounts, interest rate and FX-converted value.
   */
  const createOfferInstallmentSegments: CreateOfferInstallmentSegments = (offer, userInput, options = {}) => {
    const customizations: Customizations | undefined = offer.customizations;
    const customLoanPeriod = customizations?.customLoanPeriodInMonths;

    const to = customLoanPeriod ?? userInput?.loanPeriodInMonths ?? FROM_PERIOD;
    const chanMg = offer.changingMargin;
    const lps: LowPersonalShareIncreasingMargin | undefined = options.customLps === undefined
      ? offer.lowPersonalShareIncreasingMargin
      : (options.customLps ?? undefined);
    const firstInstallmentFx = getCellAmountFx({ offer, userInput, fxAmountCallback: (fxValues: FxValues) => fxValues.firstInstallment });
    const firstInstallmentAfterFixedInterestFx = getCellAmountFx({ offer, userInput, fxAmountCallback: (fxValues: FxValues) => fxValues.firstInstallmentAfterFixedInterest });
    const firstInstallmentAfterLPSInsuranceFx = getCellAmountFx({ offer, userInput, fxAmountCallback: (fxValues: FxValues) => fxValues.firstInstallmentAfterLPSInsurance });
    const lastInstallmentFx = getCellAmountFx({ offer, userInput, fxAmountCallback: (fxValues: FxValues) => fxValues.lastInstallment });

    const resultSegments: CalculatorOfferInstallmentSegment[] = [];

    if (chanMg) {
      if (lps) {
        if (lps.eventualPeriod < chanMg.initialPeriod) {
          const preDefinedSegments: Tuple<CalculatorOfferInstallmentSegment, 3> = [{
            to: lps.eventualPeriod,
            startAmount: offer.monthlyFees.firstInstallment,
            endAmount: offer.monthlyFees.firstInstallment,
            percentage: chanMg.initialInterestRateSum ?? 0,
            isFixedRatePeriod: chanMg.initialValueStatic ?? true,
            startFx: firstInstallmentFx,
            endFx: firstInstallmentFx
          }, {
            to: chanMg.initialPeriod,
            startAmount: typeof offer.monthlyFees.firstInstallmentAfterLPSInsurance === 'number' ? offer.monthlyFees.firstInstallmentAfterLPSInsurance : null,
            endAmount: typeof offer.monthlyFees.firstInstallmentAfterLPSInsurance === 'number' ? offer.monthlyFees.firstInstallmentAfterLPSInsurance : null,
            percentage: (chanMg.initialInterestRateSum ?? 0) - lps.increasingMargin,
            isFixedRatePeriod: chanMg.initialValueStatic ?? true,
            startFx: firstInstallmentAfterLPSInsuranceFx,
            endFx: firstInstallmentAfterLPSInsuranceFx
          }, {
            to,
            startAmount: typeof offer.monthlyFees.firstInstallmentAfterFixedInterest === 'number' ? offer.monthlyFees.firstInstallmentAfterFixedInterest : null,
            endAmount: offer.monthlyFees.lastInstallment,
            percentage: (chanMg.eventualInterestRateSum ?? 0) - lps.increasingMargin,
            isFixedRatePeriod: chanMg.eventualValueStatic ?? false,
            startFx: firstInstallmentAfterFixedInterestFx,
            endFx: lastInstallmentFx
          }];
          if (to <= lps.eventualPeriod) {
            resultSegments.push({ ...preDefinedSegments[0], to, endAmount: offer.monthlyFees.lastInstallment, endFx: lastInstallmentFx });
          } else if (to <= chanMg.initialPeriod) {
            resultSegments.push(
              { ...preDefinedSegments[0], to: lps.eventualPeriod },
              { ...preDefinedSegments[1], to, endAmount: offer.monthlyFees.lastInstallment, endFx: lastInstallmentFx },
            );
          } else {
            resultSegments.push(...preDefinedSegments);
          }
          return resultSegments;
        }
        if (lps.eventualPeriod > chanMg.initialPeriod) {
          const preDefinedSegments: Tuple<CalculatorOfferInstallmentSegment, 3> = [{
            to: chanMg.initialPeriod,
            startAmount: offer.monthlyFees.firstInstallment,
            endAmount: offer.monthlyFees.firstInstallment,
            percentage: chanMg.initialInterestRateSum ?? 0,
            isFixedRatePeriod: chanMg.initialValueStatic ?? true,
            startFx: firstInstallmentFx,
            endFx: firstInstallmentFx
          }, {
            to: lps.eventualPeriod,
            startAmount: typeof offer.monthlyFees.firstInstallmentAfterFixedInterest === 'number' ? offer.monthlyFees.firstInstallmentAfterFixedInterest : null,
            endAmount: typeof offer.monthlyFees.firstInstallmentAfterFixedInterest === 'number' ? offer.monthlyFees.firstInstallmentAfterFixedInterest : null,
            percentage: chanMg.eventualInterestRateSum ?? 0,
            isFixedRatePeriod: chanMg.eventualValueStatic ?? false,
            startFx: firstInstallmentAfterFixedInterestFx,
            endFx: firstInstallmentAfterFixedInterestFx
          }, {
            to,
            startAmount: typeof offer.monthlyFees.firstInstallmentAfterLPSInsurance === 'number' ? offer.monthlyFees.firstInstallmentAfterLPSInsurance : null,
            endAmount: offer.monthlyFees.lastInstallment,
            percentage: (chanMg.eventualInterestRateSum ?? 0) - lps.increasingMargin,
            isFixedRatePeriod: chanMg.eventualValueStatic ?? false,
            startFx: firstInstallmentAfterLPSInsuranceFx,
            endFx: lastInstallmentFx
          }];
          if (to <= chanMg.initialPeriod) {
            resultSegments.push({ ...preDefinedSegments[0], to, endAmount: offer.monthlyFees.lastInstallment, endFx: lastInstallmentFx });
          } else if (to <= lps.eventualPeriod) {
            resultSegments.push(
              { ...preDefinedSegments[0], to: chanMg.initialPeriod },
              { ...preDefinedSegments[1], to, endAmount: offer.monthlyFees.lastInstallment, endFx: lastInstallmentFx },
            );
          } else {
            resultSegments.push(...preDefinedSegments);
          }
          return resultSegments;
        }
        const preDefinedSegments: Tuple<CalculatorOfferInstallmentSegment, 2> = [{
          to: chanMg.initialPeriod,
          startAmount: offer.monthlyFees.firstInstallment,
          endAmount: offer.monthlyFees.firstInstallment,
          percentage: chanMg.initialInterestRateSum ?? 0,
          isFixedRatePeriod: chanMg.initialValueStatic ?? true,
          startFx: firstInstallmentFx,
          endFx: firstInstallmentFx
        }, {
          to,
          startAmount: typeof offer.monthlyFees.firstInstallmentAfterFixedInterest === 'number' ? offer.monthlyFees.firstInstallmentAfterFixedInterest : null,
          endAmount: offer.monthlyFees.lastInstallment,
          percentage: (chanMg.eventualInterestRateSum ?? 0) - lps.increasingMargin,
          isFixedRatePeriod: chanMg.eventualValueStatic ?? false,
          startFx: firstInstallmentAfterFixedInterestFx,
          endFx: lastInstallmentFx
        }];
        if (to <= chanMg.initialPeriod) {
          resultSegments.push({ ...preDefinedSegments[0], to, endAmount: offer.monthlyFees.lastInstallment, endFx: lastInstallmentFx });
        } else {
          resultSegments.push(...preDefinedSegments);
        }
        return resultSegments;
      }
      const preDefinedSegments: Tuple<CalculatorOfferInstallmentSegment, 2> = [{
        to: chanMg.initialPeriod,
        startAmount: offer.monthlyFees.firstInstallment,
        endAmount: offer.monthlyFees.firstInstallment,
        percentage: chanMg.initialInterestRateSum ?? 0,
        isFixedRatePeriod: chanMg.initialValueStatic ?? true,
        startFx: firstInstallmentFx,
        endFx: firstInstallmentFx
      }, {
        to,
        startAmount: typeof offer.monthlyFees.firstInstallmentAfterFixedInterest === 'number' ? offer.monthlyFees.firstInstallmentAfterFixedInterest : null,
        endAmount: offer.monthlyFees.lastInstallment,
        percentage: chanMg.eventualInterestRateSum ?? 0,
        isFixedRatePeriod: chanMg.eventualValueStatic ?? false,
        startFx: firstInstallmentAfterFixedInterestFx,
        endFx: lastInstallmentFx
      }];
      if (to <= chanMg.initialPeriod) {
        resultSegments.push({ ...preDefinedSegments[0], to, endAmount: offer.monthlyFees.lastInstallment, endFx: lastInstallmentFx });
      } else {
        resultSegments.push(...preDefinedSegments);
      }
      return resultSegments;
    }
    if (lps && FROM_PERIOD < lps.eventualPeriod && lps.eventualPeriod < to) {
      return [{
        to: lps.eventualPeriod,
        startAmount: offer.monthlyFees.firstInstallment,
        endAmount: offer.monthlyFees.firstInstallment,
        percentage: offer.interestRate.sum,
        isFixedRatePeriod: false,
        startFx: firstInstallmentFx,
        endFx: firstInstallmentFx
      }, {
        to,
        startAmount: typeof offer.monthlyFees.firstInstallmentAfterLPSInsurance === 'number' ? offer.monthlyFees.firstInstallmentAfterLPSInsurance : null,
        endAmount: offer.monthlyFees.lastInstallment,
        percentage: offer.interestRate.sum - lps.increasingMargin,
        isFixedRatePeriod: false,
        startFx: firstInstallmentAfterLPSInsuranceFx,
        endFx: lastInstallmentFx
      }];
    }
    return [{
      to,
      startAmount: offer.monthlyFees.firstInstallment,
      endAmount: offer.monthlyFees.lastInstallment,
      percentage: offer.interestRate.sum,
      isFixedRatePeriod: false,
      startFx: firstInstallmentFx,
      endFx: lastInstallmentFx
    }];
  };

  return { createOfferInstallmentSegments };
};